'use client'
import { Globe } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { THome_Config } from "@/app/config";
import { motion, useInView } from "motion/react";
import { useRef } from "react";

export const WhyChooseZumaSection = ({config}:{config:THome_Config['whyChooseZuma']})=> {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.6,
      },
    },
  };

  
return (
    <motion.section 
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={containerVariants}
      className="w-full py-12 md:py-20 lg:py-[84px] bg-[#022c43]"
    >
      <div className="max-w-[1120px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-14 items-start">
          {/* Left Content Section */}
          <motion.div 
            variants={ {
              hidden: { 
                opacity: 0, 
                x: -50,
              },
              visible: {
                opacity: 1,
                x: 0,
                transition: {
                  duration: 0.8,
                  ease: [0.25, 0.25, 0.25, 0.75],
                },
              },
            }}
            className="flex flex-col gap-6 md:gap-8 lg:gap-[27.1px] order-1"
          >
            {/* Header */}
            <motion.header 
              variants={ {
                hidden: { 
                  opacity: 0, 
                  y: 30,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
              className="w-full"
            >
              <h1 className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-white text-2xl sm:text-3xl md:text-4xl lg:text-[42px] tracking-[0] leading-tight lg:leading-[42px]">
                {config.title}
              </h1>
            </motion.header>

            {/* Description */}
            <motion.div 
              variants={ {
                hidden: { 
                  opacity: 0, 
                  y: 30,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
              className="w-full"
            >
              <p className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#ffecd4] text-base md:text-lg lg:text-[20.8px] tracking-[0] leading-relaxed lg:leading-[34.1px]">
                {config.description}
              </p>
            </motion.div>

            {/* Statistics Grid */}
            <motion.div 
              variants={ {
                hidden: { 
                  opacity: 0, 
                  y: 30,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
              className="w-full grid grid-cols-2 gap-x-6 gap-y-4 md:gap-x-12 md:gap-y-6 lg:gap-x-[109px] lg:gap-y-[14px]"
            >
              {config.statistics.map((stat, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, scale: 0.8 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                  className="flex flex-col items-center gap-2 lg:gap-[7px]"
                >
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ 
                      duration: 0.6, 
                      delay: 0.8 + index * 0.1,
                      ease: "easeOut"
                    }}
                    className="text-white text-center [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0] leading-tight lg:leading-[35px] text-xl md:text-2xl lg:text-3xl"
                  >
                    {stat.value}
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ 
                      duration: 0.6, 
                      delay: 1.0 + index * 0.1 
                    }}
                    className="text-[#ffecd4] text-center [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0] leading-tight lg:leading-[21px] text-xs md:text-sm lg:text-[14px]"
                  >
                    {stat.label}
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Card Section */}
          <motion.div
            variants={{
              hidden: { 
                opacity: 0, 
                x: 50,
              },
              visible: {
                opacity: 1,
                x: 0,
                transition: {
                  duration: 0.8,
                  ease: [0.25, 0.25, 0.25, 0.75],
                  delay: 0.3,
                },
              },
            }}
            whileHover={{ 
              y: -5,
              transition: { duration: 0.3, ease: "easeOut" }
            }}
          >
            <Card className="bg-[#fffefe1a] border-[#fffefe33] backdrop-blur-sm backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)] rounded-xl lg:rounded-[12.75px] order-2">
              <CardContent className="p-6 md:p-8 lg:p-[29px] flex flex-col gap-6 md:gap-8 lg:gap-7">
                {/* Card Header */}
                <motion.div 
                  variants={{
                    hidden: { 
                      opacity: 0, 
                      scale: 0.8,
                    },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                  }}
                  className="flex flex-col items-center gap-3 md:gap-4 lg:gap-3.5"
                >
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                    transition={{ 
                      duration: 0.8, 
                      delay: 0.6,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ 
                      scale: 1.1,
                      rotate: 360,
                      transition: { duration: 0.8 }
                    }}
                    className="flex w-16 h-16 md:w-18 md:h-18 lg:w-[70px] lg:h-[70px] items-center justify-center bg-[#F54900]/20 rounded-full"
                  >
                    <Globe className="w-8 h-8 md:w-9 md:h-9 lg:w-[35px] lg:h-[35px] text-white" />
                  </motion.div>

                  <motion.h2 
                    variants={ {
                      hidden: { 
                        opacity: 0, 
                        y: 30,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                        },
                      },
                    }}
                    className="text-white text-lg md:text-xl lg:text-[21px] text-center leading-tight lg:leading-7 [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]"
                  >
                    {config.globalCardTitle}
                  </motion.h2>

                  <motion.p 
                    variants={ {
                      hidden: { 
                        opacity: 0, 
                        y: 30,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                        },
                      },
                    }}
                    className="text-[#ffecd4] text-sm md:text-base lg:text-sm text-center [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0] leading-relaxed lg:leading-[21px]"
                  >
                    {config.globalCardDescription}
                  </motion.p>
                </motion.div>

                {/* Progress Bars */}
                <motion.div 
                  variants={ {
                    hidden: { 
                      opacity: 0, 
                      y: 30,
                    },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                  }}
                  className="flex flex-col gap-4 md:gap-5 lg:gap-3.5"
                >
                  {config.progressData.map((item, index) => (
                    <motion.div 
                      key={index} 
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                      transition={{ 
                        duration: 0.6, 
                        delay: 1.0 + index * 0.2,
                        ease: "easeOut"
                      }}
                      className="flex flex-col gap-3 lg:gap-3.5"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white text-sm md:text-base lg:text-sm [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0] leading-tight lg:leading-[21px]">
                          {item.label}
                        </span>
                        <motion.span 
                          initial={{ opacity: 0 }}
                          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                          transition={{ 
                            duration: 0.6, 
                            delay: 1.5 + index * 0.2 
                          }}
                          className="text-white text-sm md:text-base lg:text-sm [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0] leading-tight lg:leading-[21px]"
                        >
                          {item.value}
                        </motion.span>
                      </div>
                      <div className="w-full h-2 md:h-2.5 lg:h-[7px] bg-[#F54900]/20 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={isInView ? { width: `${item.progress}%` } : { width: 0 }}
                          transition={{
                            duration: 1.5,
                            delay: 1.2 + index * 0.2,
                            ease: "easeInOut"
                          }}
                          className="h-full bg-white rounded-full"
                        />
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};